/*
 * TWC header behaviour — desktop dropdown / mega-menu open-close, mobile
 * drawer triggers and tier drilling, and the member-only delivery-box badge.
 *
 * Everything hangs off one AbortController so a Shopify design-mode section
 * reload (or a Section Rendering morph) can tear the listeners down cleanly.
 * The two `shopify:section:*` hooks at the bottom are the deliberate exception
 * — see the comment there.
 *
 * Badge economics: guests cost ZERO engine calls — the config <script> is only
 * emitted inside {% if customer %}. On account pages TwcMemberData boots its
 * seven GETs and broadcasts the box, so there we subscribe for the badge; on
 * every other page the count costs one cheap box/summary GET, cached in
 * sessionStorage for the browsing session under a customer-scoped key (a
 * shared key leaks a count across logins). The card-expiry flag rides ONLY
 * box/summary, so a session whose cache has never stored it makes that same
 * single GET wherever it starts — account pages included (backfill mode:
 * card-expiry surfaces only). The stored flag is good for CARD_EXPIRY_TTL_MS
 * and is dropped early by a `twc:payment-method-changed` event, so a member
 * who changes a card sees the bar change in the same tab. Any failure
 * renders the bag with no numeral. Never an error state.
 */

import { lockScroll } from '@theme/utilities';

const CACHE_PREFIX = 'twc_box_summary';
const HOVER_CLOSE_DELAY = 180;

/* How long a cached card-expiry answer stands before box/summary is asked
 * again. A card added or removed on Shopify's hosted profile page sends no
 * event to this tab, so the next page load after this window re-asks. */
const CARD_EXPIRY_TTL_MS = 10 * 60 * 1000;

/* Sent by assets/twc-personal-details.js after a successful wallet write. */
const PAYMENT_METHOD_CHANGED_EVENT = 'twc:payment-method-changed';

/* The marker assets/twc-member-data.js itself boots its seven GETs on. */
const ACCOUNT_PAGE_SELECTOR = '[data-twc-account-page], .twc-account-sidebar';

/* Ours to promote to true overlays; native drawers keep Horizon's behaviour. */
const TWC_DRAWER_IDS = ['twc-nav-drawer', 'twc-account-drawer'];

let controller = null;
let closeTimer = null;
let memberUnsub = null;
let cacheKey = null;
let customerId = '';

/* ---- Panels (mega menu, simple dropdowns, account menu) ----------------- */

function panelFor(trigger) {
  const id = trigger.getAttribute('data-twc-panel');
  return id ? document.getElementById(id) : null;
}

function setPanel(trigger, open) {
  const panel = panelFor(trigger);
  trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
  if (panel) panel.hidden = !open;
}

function closePanels(header, except) {
  for (const trigger of header.querySelectorAll('[data-twc-panel]')) {
    if (trigger !== except) setPanel(trigger, false);
  }
}

function cancelClose() {
  if (closeTimer) {
    clearTimeout(closeTimer);
    closeTimer = null;
  }
}

function scheduleClose(header) {
  cancelClose();
  closeTimer = setTimeout(() => closePanels(header), HOVER_CLOSE_DELAY);
}

function wirePanels(header, signal) {
  const triggers = header.querySelectorAll('[data-twc-panel]');
  if (!triggers.length) return;

  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  for (const trigger of triggers) {
    trigger.addEventListener(
      'click',
      (event) => {
        const open = trigger.getAttribute('aria-expanded') === 'true';
        cancelClose();
        closePanels(header, trigger);
        setPanel(trigger, !open);

        // Keyboard activation (Enter / Space) reports detail 0. The mega panels
        // render outside the nav list so they can span the header, which means
        // Tab from the trigger would walk straight past them to the next menu
        // item — move focus into the panel instead. Tab then steps through the
        // panel's links, and the focusin watcher below closes it on the way out.
        if (!open && event.detail === 0) {
          const panel = panelFor(trigger);
          const first = panel && panel.querySelector('a[href], button:not([disabled])');
          if (first instanceof HTMLElement) first.focus();
        }
      },
      { signal }
    );

    if (!canHover) continue;

    const panel = panelFor(trigger);
    const region = trigger.closest('.twc-hdr__nav-item, .twc-hdr__account') || trigger;

    region.addEventListener(
      'mouseenter',
      () => {
        cancelClose();
        closePanels(header, trigger);
        setPanel(trigger, true);
      },
      { signal }
    );
    region.addEventListener('mouseleave', () => scheduleClose(header), { signal });

    if (panel) {
      panel.addEventListener('mouseenter', cancelClose, { signal });
      panel.addEventListener('mouseleave', () => scheduleClose(header), { signal });
    }
  }

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (target instanceof Node && header.contains(target)) return;
      closePanels(header);
    },
    { signal }
  );

  // A mega panel is a sibling of the header row, not a child of its trigger, so
  // no single element can own a focusout. Watch focus at the document instead
  // and close as soon as it leaves the trigger + panel pair.
  document.addEventListener(
    'focusin',
    (event) => {
      const trigger = header.querySelector('[data-twc-panel][aria-expanded="true"]');
      if (!trigger) return;
      const target = event.target;
      if (!(target instanceof Node) || trigger.contains(target)) return;
      const panel = panelFor(trigger);
      if (panel && panel.contains(target)) return;
      closePanels(header);
    },
    { signal }
  );

  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Escape') return;
      const open = header.querySelector('[data-twc-panel][aria-expanded="true"]');
      if (!open) return;
      closePanels(header);
      if (open instanceof HTMLElement) open.focus();
    },
    { signal }
  );
}

/* ---- Drawers (Horizon <theme-drawer> primitive) ------------------------ */

/**
 * Calls a method on a <theme-drawer>, tolerating the upgrade race: the element
 * can be in the DOM before assets/theme-drawer.js (fetchpriority="low") has
 * executed, in which case open()/close() do not exist yet.
 *
 * @param {string} id - The drawer element id.
 * @param {'open' | 'close' | 'toggle'} method - Method to invoke.
 */
function drawerCall(id, method) {
  const drawer = document.getElementById(id);
  if (!drawer) return;

  const invoke = () => {
    const fn = /** @type {any} */ (drawer)[method];
    if (typeof fn === 'function') fn.call(drawer);
  };

  if (typeof (/** @type {any} */ (drawer)[method]) === 'function') {
    invoke();
    return;
  }

  if (window.customElements) {
    window.customElements.whenDefined('theme-drawer').then(() => {
      window.customElements.upgrade(drawer);
      invoke();
    });
  }
}

/**
 * Promotes an open drawer panel into the browser's top layer.
 *
 * Above 990px Horizon's <theme-drawer> is a SIDEBAR: it opens the dialog with
 * show() — non-modal, so there is no ::backdrop and the sticky header paints
 * over it — and squeezes .page-wrapper sideways (cancelled in the section CSS).
 * Every twc drawer is a true overlay at every width, so we re-open with
 * showModal(): top layer beats every z-index and the scrim
 * becomes the dialog's own ::backdrop. Below 990px Horizon already does this.
 * sections/twc-box-drawer.liquid promotes its drawer exactly the same way.
 *
 * @param {string} id - The drawer element id.
 */
function promoteToModal(id) {
  const drawer = document.getElementById(id);
  const panel = drawer && drawer.querySelector('dialog');
  if (!panel || !panel.open) return;

  try {
    if (panel.matches(':modal')) return; // already modal (narrow viewports)
  } catch (error) {
    return; // browser without :modal support — the CSS z-index fallback covers it
  }

  panel.close();
  panel.showModal();
  // Horizon's theme-drawer released its scroll lock when the crossing re-opened
  // the panel in sidebar mode; a modal without the lock leaves the page
  // scrollable behind it. Same owner object, so Horizon's own close path
  // releases this one too.
  lockScroll(panel);

  const closeButton = panel.querySelector('.twc-drawer__panel:not([hidden]) .theme-drawer__close-button');
  if (closeButton instanceof HTMLElement) closeButton.focus();
}

/**
 * Opens one of our drawers with its panel ALREADY in the browser's top layer,
 * so the slide-in plays from the first frame.
 *
 * promoteToModal() above runs from the theme-drawer:open handler, which fires
 * inside ThemeDrawer.open() — after it has started the slide-in. Its
 * close()/showModal() pair cancels that animation, and because
 * onAnimationEnd() (assets/utilities.js) is Promise.allSettled over
 * animation.finished, the cancellation counts as "ended" and the --opening
 * class is stripped a microtask later: the re-shown panel had nothing left to
 * animate and snapped into place (measured at 1000px: the whole 400px in one
 * frame). Pre-empting the promotion leaves the animation alone. The only thing
 * to neutralise is ThemeDrawer's one dialog.show() call — the panel is already
 * open AND modal by then, so show() would throw InvalidStateError. Below 990px
 * it calls showModal(), a spec no-op on an already-modal dialog.
 *
 * Everything else stays Horizon's: the [open] attribute, stack order, focus
 * trap and focus return, Escape, backdrop click, the animated close, and the
 * theme-drawer:open/close events. One handler path per gesture.
 *
 * @param {string} id - The drawer element id.
 */
function openPromoted(id) {
  const drawer = document.getElementById(id);
  const panel = drawer && drawer.querySelector('dialog');

  // Not upgraded yet, no panel, or mid-close (theme-drawer clears [open] before
  // its slide-out ends): hand back to Horizon, promoteToModal() still catches it.
  if (!panel || panel.open || typeof (/** @type {any} */ (drawer).open) !== 'function') {
    drawerCall(id, 'open');
    return;
  }

  let promoted = false;
  try {
    panel.showModal();
    promoted = panel.matches(':modal');
  } catch (error) {
    promoted = false; // no top layer available — promoteToModal() still tries
  }

  if (!promoted) {
    drawerCall(id, 'open');
    return;
  }

  panel.show = () => {}; // own property shadows the prototype method
  try {
    /** @type {any} */ (drawer).open();
  } finally {
    delete panel.show;
  }
}

/**
 * Toggles a drawer, pre-empting the top-layer promotion on the way open.
 *
 * @param {string} id - The drawer element id.
 */
function toggleDrawer(id) {
  const drawer = document.getElementById(id);

  if (drawer && TWC_DRAWER_IDS.includes(id) && !drawer.hasAttribute('open')) {
    openPromoted(id);
    return;
  }

  drawerCall(id, 'toggle');
}

/**
 * Shows one tier of the nav drawer.
 *
 * @param {HTMLElement} dialog - The drawer dialog.
 * @param {string} name - The data-twc-tier value to show.
 * @param {boolean} [moveFocus] - Move focus into the tier. Off while closing:
 *   the dialog is still open during its slide-out, so focusing there would
 *   strand focus on an element that is about to stop being rendered.
 */
function showTier(dialog, name, moveFocus) {
  const panels = dialog.querySelectorAll('[data-twc-tier]');
  for (const panel of panels) {
    panel.hidden = panel.getAttribute('data-twc-tier') !== name;
  }
  if (moveFocus === false) return;
  // Document order, not selector order: a drilled-in tier leads with its Back
  // button, the root tier has only the close control in its head.
  const active = dialog.querySelector(`[data-twc-tier="${name}"]`);
  const focusable = active && active.querySelector('.twc-drawer__back, .theme-drawer__close-button');
  if (focusable instanceof HTMLElement) focusable.focus();
}

function wireDrawers(header, signal) {
  header.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const opener = target.closest('[data-twc-drawer-open]');
      if (opener) {
        toggleDrawer(opener.getAttribute('data-twc-drawer-open') || '');
        return;
      }

      const closer = target.closest('[data-twc-drawer-close]');
      if (closer) {
        drawerCall(closer.getAttribute('data-twc-drawer-close') || '', 'close');
        return;
      }

      const tier = target.closest('[data-twc-tier-open]');
      if (tier) {
        const dialog = tier.closest('dialog');
        if (dialog) showTier(dialog, tier.getAttribute('data-twc-tier-open') || 'root');
        return;
      }

      // The delivery-box drawer owns its own section; announce the intent and
      // let it respond.
      if (target.closest('[data-twc-bag]')) {
        document.dispatchEvent(new CustomEvent('twc:box-drawer:toggle'));
      }
    },
    { signal }
  );

  // Reflect drawer state on whichever button controls it.
  const reflect = (event, open) => {
    const drawer = event.target;
    if (!(drawer instanceof Element) || !drawer.id) return;
    for (const trigger of header.querySelectorAll(`[aria-controls="${drawer.id}"]`)) {
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    const ours = TWC_DRAWER_IDS.includes(drawer.id);
    const dialog = drawer.querySelector('dialog');

    if (open) {
      if (ours) promoteToModal(drawer.id);
      return;
    }

    // Always hand the nav drawer back at its root tier.
    if (dialog && dialog.querySelector('[data-twc-tier]')) showTier(dialog, 'root', false);
  };

  document.addEventListener('theme-drawer:open', (event) => reflect(event, true), { signal });
  document.addEventListener('theme-drawer:close', (event) => reflect(event, false), { signal });

  // Crossing 990px re-opens an open panel in Horizon's non-modal sidebar mode;
  // promote it again once that handler has run.
  window.matchMedia('(min-width: 990px)').addEventListener(
    'change',
    (event) => {
      // Horizon switches the scroll root at this boundary (assets/base.css):
      // the window scrolls below it, .page-wrapper above it — and nothing
      // moves the offset across. A resize past the boundary strands the
      // offset on the old root; at >=990 a stale window offset shoves the
      // fixed-height wrapper off-screen and the page paints white. Exactly
      // one root legitimately holds the offset, so merge
      // whatever both report and hand the total to the new root. 'instant'
      // sidesteps the wrapper's scroll-behavior: smooth; the rAF lets the new
      // layout apply before the writes.
      const wrapper = document.querySelector('.page-wrapper');
      if (wrapper) {
        const total = window.scrollY + wrapper.scrollTop;
        requestAnimationFrame(() => {
          if (event.matches) {
            wrapper.scrollTo({ top: total, behavior: 'instant' });
            window.scrollTo({ top: 0, behavior: 'instant' });
          } else {
            window.scrollTo({ top: total, behavior: 'instant' });
          }
        });
      }

      setTimeout(() => {
        for (const id of TWC_DRAWER_IDS) promoteToModal(id);
      }, 0);
    },
    { signal }
  );
}

/* ---- Delivery-box badge (members only) --------------------------------- */

function readConfig() {
  const node = document.querySelector('script[data-twc-header-config]');
  if (!node || !node.textContent) return null;
  try {
    return JSON.parse(node.textContent);
  } catch (error) {
    return null;
  }
}

function renderBadge(header, count) {
  const badge = header.querySelector('[data-twc-bag-count]');
  if (!(badge instanceof HTMLElement)) return;

  const value = Number(count);
  if (!Number.isFinite(value) || value <= 0) {
    badge.hidden = true;
    badge.textContent = '';
    return;
  }

  badge.textContent = value > 99 ? '99+' : String(value);
  badge.hidden = false;
}

/* The member's profile photo, layered over the Liquid-rendered initials.
 * Liquid paints it FIRST from the twc.avatar customer metafield (see
 * snippets/twc-account-avatar-src.liquid), so this is the correction layer, not
 * the only source: the url also rides the SAME box/summary payload the badge
 * already needs — no extra request — and the sessionStorage cache below means
 * repeat navigations paint it with no network. `undefined` means "not in this
 * payload" (leave whatever is showing, including whatever Liquid rendered);
 * null means "no photo" (fall back to initials). The chip is 38px, so ask the
 * CDN for 80 — retina without pulling the stored 512. The sized url below MUST
 * stay byte-identical to the one that snippet emits, or every page load
 * re-assigns src and downloads the same face twice. */
function renderAvatar(header, url) {
  if (typeof url === 'undefined') return;
  const images = header.querySelectorAll('[data-twc-chip-avatar]');
  for (const img of images) {
    if (!(img instanceof HTMLImageElement)) continue;
    if (!url) {
      img.hidden = true;
      img.removeAttribute('src');
      continue;
    }
    const sized = url.includes('?')
      ? `${url}&width=80&height=80&crop=center`
      : `${url}?width=80&height=80&crop=center`;
    if (img.getAttribute('src') !== sized) img.setAttribute('src', sized);
    img.hidden = false;
  }
}

/* The site-wide card-expiry information bar
 * (sections/twc-card-expiry-bar.liquid — hidden Liquid band, revealed here).
 * `state` is the engine's box/summary `cardExpiry`: 'expired' | 'soon' | null,
 * computed SERVER-side from the CURRENT payment method in the store's
 * calendar — never recomputed client-side for this surface. `undefined` means
 * "this payload does not carry the field" (old cache records): leave whatever
 * is showing.
 *
 * SOURCE CHOICE for account pages: the TwcMemberData broadcast that feeds
 * the badge there does NOT carry cardExpiry — only box/summary does — so on
 * account pages the bar renders from the sessionStorage cache when the flag
 * is already known, and otherwise initBadge issues the ONE box/summary GET
 * in backfill mode (card-expiry surfaces only). Once the engine has
 * answered — warning or explicit "card fine" — the flag is cached with a
 * timestamp, and no further summary request is made until the flag is
 * older than CARD_EXPIRY_TTL_MS or a wallet write drops it. */
function renderCardAlert(state) {
  if (typeof state === 'undefined') return;
  const value = state === 'expired' || state === 'soon' ? state : null;

  const bar = document.querySelector('[data-twc-cardbar]');
  if (bar instanceof HTMLElement) {
    if (value) {
      bar.setAttribute('data-expiry', value);
      for (const msg of bar.querySelectorAll('[data-twc-cardbar-msg]')) {
        msg.hidden = msg.getAttribute('data-twc-cardbar-msg') !== value;
      }
      bar.hidden = false;
    } else {
      bar.hidden = true;
    }
  }

  // The avatar "!" badges (header chips + account drawer), the sr-only
  // "payment method needs attention" labels beside them, and the drawer's
  // UPDATE pill all share one Liquid-rendered hook. Expired and soon draw
  // the same badge — the bar and the payment page carry the distinction.
  for (const el of document.querySelectorAll('[data-twc-card-warn]')) {
    el.hidden = !value;
  }

  // The drawer-mode chip names itself with aria-label, which overrides any
  // sr-only descendant — swap the whole label instead (both variants are
  // Liquid-stamped, so the copy stays in the locale file).
  for (const btn of document.querySelectorAll('[data-twc-warn-aria]')) {
    const label = value ? btn.getAttribute('data-twc-warn-aria') : btn.getAttribute('data-twc-base-aria');
    if (label) btn.setAttribute('aria-label', label);
  }
}

function readCache() {
  if (!cacheKey) return null;
  try {
    const raw = sessionStorage.getItem(cacheKey);
    const cached = raw ? JSON.parse(raw) : null;
    if (!cached || typeof cached.count === 'undefined') return null;
    // Belt and braces: the key is customer-scoped AND the payload carries the
    // id, so a count cached for somebody else can never reach the bag.
    if (String(cached.customerId) !== customerId) return null;
    return cached;
  } catch (error) {
    return null;
  }
}

function writeCache(count, phase, avatarUrl, cardExpiry) {
  if (!cacheKey) return;
  try {
    // MERGE-PRESERVING WRITE. This record has TWO writers —
    // this file and assets/twc-box-drawer.js writeSummary() — and several
    // callers here, each knowing only the fields its own payload carries:
    // the member-data broadcast has the avatar but never cardExpiry, the
    // box/summary fetch has both, the drawer knows only the count. An
    // absent (undefined) field keeps the previous cached value, so a
    // count-only write can never wipe the avatar url or the card-expiry
    // flag and flicker those surfaces off on the next page.
    const previous = avatarUrl === undefined || cardExpiry === undefined ? readCache() : null;
    const record = {
      count,
      phase: phase || null,
      avatarUrl: avatarUrl === undefined ? (previous && previous.avatarUrl) ?? null : avatarUrl,
      customerId,
    };
    // cardExpiry is TRI-STATE in this record: 'expired'/'soon', null (the
    // box/summary fetch answered "card fine"), or ABSENT (the fetch has
    // never answered). Only that fetch passes the parameter; every other
    // caller passes undefined, which carries the stored state forward — and
    // when no stored record holds the key, the key STAYS absent. Seeding a
    // null here would read back as authoritative "card fine" and initBadge
    // would never fetch the summary for the session, so a member landing on
    // an account page first would never see the bar.
    // cardExpiryAt is the time of the engine's answer. cardExpiryFresh()
    // reads it; a carried-forward flag keeps the original time, so a
    // count-only write never extends the answer's life.
    if (cardExpiry !== undefined) {
      record.cardExpiry = cardExpiry;
      record.cardExpiryAt = Date.now();
    } else if (previous && 'cardExpiry' in previous) {
      record.cardExpiry = previous.cardExpiry;
      if (typeof previous.cardExpiryAt === 'number') record.cardExpiryAt = previous.cardExpiryAt;
    }
    sessionStorage.setItem(cacheKey, JSON.stringify(record));
  } catch (error) {
    /* private mode / quota — the badge simply refetches next page */
  }
}

/* A cached flag counts as known only while it is younger than the TTL. A
 * record that carries the flag with no timestamp comes from a session that
 * predates the timestamp: it reads as unknown, so one fetch re-stamps it. */
function cardExpiryFresh(cached) {
  if (!cached || !('cardExpiry' in cached)) return false;
  if (typeof cached.cardExpiryAt !== 'number') return false;
  return Date.now() - cached.cardExpiryAt < CARD_EXPIRY_TTL_MS;
}

/* Drop the flag (and its timestamp) so the next initBadge or fetchSummary
 * asks the engine again. The count and avatar stay. */
function forgetCardExpiry() {
  const cached = readCache();
  if (!cached || !('cardExpiry' in cached)) return;
  try {
    delete cached.cardExpiry;
    delete cached.cardExpiryAt;
    sessionStorage.setItem(cacheKey, JSON.stringify(cached));
  } catch (error) {
    /* private mode / quota — nothing cached to forget */
  }
}

function countFromBox(box) {
  if (!box || !Array.isArray(box.lines)) return null;
  return box.lines.reduce((total, line) => {
    const qty = Number(line && (line.qty != null ? line.qty : line.quantity));
    return total + (Number.isFinite(qty) ? qty : 0);
  }, 0);
}

function subscribeToMemberData(header) {
  const store = window.TwcMemberData;
  if (!store || typeof store.subscribe !== 'function') return false;
  if (memberUnsub) return true;

  // Read-only: islands on account pages own the fetching, we just re-render.
  memberUnsub = store.subscribe((state) => {
    // Account pages: /me carries the avatar url, /box/current the count. They
    // arrive in the same broadcast, so paint both (either may be absent).
    // The broadcast never carries cardExpiry (only box/summary does), so the
    // card-expiry surfaces are NOT repainted here — they hold the cached
    // value, and the merge-preserving writeCache below keeps that value
    // intact through these count/avatar writes (see renderCardAlert).
    const profile = state && state.me ? state.me.profile : null;
    const avatarUrl = profile ? profile.avatarUrl || null : undefined;
    renderAvatar(header, avatarUrl);
    let count = countFromBox(state && state.box);
    // A paused/unsubscribed membership keeps its basket engine-side, but the
    // bag must not advertise a box that will not ship — the
    // count reads 0 (the badge hides at <= 0) and the cache stays consistent
    // with the drawer's dormant write. Resume repaints via this broadcast.
    const cycle = state && state.cycleState;
    if (cycle === 'paused' || cycle === 'unsubscribed') count = 0;
    if (count == null) {
      if (avatarUrl !== undefined) writeCache(readCache()?.count ?? 0, state && state.phase, avatarUrl);
      return;
    }
    writeCache(count, state && state.phase, avatarUrl);
    renderBadge(header, count);
  });
  return true;
}

/* A read-only member preview tab (snippets/twc-preview.liquid). Its badge,
 * avatar and card flag belong to the previewed member, not to whoever is
 * signed in, so the tab reads and writes no summary cache and the one
 * box/summary GET carries the preview token. */
function previewToken() {
  const preview = window.TWC_PREVIEW;
  if (!preview || !preview.active) return null;
  try {
    return sessionStorage.getItem('twc_preview');
  } catch (error) {
    return null;
  }
}

function initBadge(header, config, signal) {
  customerId = config.customerId == null ? '' : String(config.customerId);
  cacheKey = previewToken() ? null : `${CACHE_PREFIX}:${customerId}`;

  try {
    // Anything under the old shared key could belong to a different customer.
    if (cacheKey) sessionStorage.removeItem(CACHE_PREFIX);
  } catch (error) {
    /* private mode — nothing to clear */
  }

  const cached = readCache();
  if (cached) {
    renderBadge(header, cached.count);
    // `undefined`, not null, on a cache with no url: the section has just
    // rendered the photo from the twc.avatar metafield, which is this request's
    // server-side truth, and a cached null is only ever a stale hint. Erasing a
    // server-rendered src here would be unrecoverable on a non-account page —
    // a cache hit skips the box/summary fetch below, so nothing would paint it
    // back. A cached URL still repaints instantly, which is the point of the
    // cache; an authoritative null still clears the photo when the store or
    // box/summary says so.
    renderAvatar(header, cached.avatarUrl || undefined);
    // A record with no cardExpiry key is unknown, so the bar stays as Liquid
    // left it (hidden). A stored null is authoritative "card fine" and keeps
    // it hidden explicitly.
    renderCardAlert('cardExpiry' in cached ? cached.cardExpiry || null : undefined);
  }

  // The sidebar's upload island announces a change; repaint without a reload.
  document.addEventListener(
    'twc:avatar-changed',
    (event) => {
      const url = event.detail ? event.detail.avatarUrl || null : null;
      renderAvatar(header, url);
      const current = readCache();
      writeCache(current ? current.count : 0, current ? current.phase : null, url);
    },
    { signal },
  );

  // Account pages boot TwcMemberData, whose /box/current lands in the broadcast
  // we subscribe to below — asking box/summary for the same NUMBER would be a
  // redundant eighth call on every account page load. Everywhere else the store
  // script may be present (the box drawer loads it) but nobody boots it, so the
  // one cheap summary GET is what pays for the badge.
  const bootsMemberData = Boolean(document.querySelector(ACCOUNT_PAGE_SELECTOR));

  // The payment-methods island announces a wallet write. The cached flag was
  // computed from the previous current card, so drop it and ask again; the
  // badge and avatar are already owned, so this is the backfill shape.
  document.addEventListener(
    PAYMENT_METHOD_CHANGED_EVENT,
    () => {
      forgetCardExpiry();
      fetchSummary(header, config, bootsMemberData || Boolean(readCache()));
    },
    { signal },
  );

  if (!subscribeToMemberData(header)) {
    // The store script loads deferred — retry once the document is ready.
    document.addEventListener('DOMContentLoaded', () => subscribeToMemberData(header), {
      once: true,
      signal,
    });
  }

  // The badge and avatar are already owned (broadcast or cache) — but the
  // card-expiry flag rides ONLY the box/summary payload, so the fetch-skip
  // holds only while the cached record carries a FRESH key ("known",
  // including a stored null = "card fine", younger than the TTL). With the
  // key absent or stale — fresh session, an old record, a summary attempt
  // that never answered, or an answer older than CARD_EXPIRY_TTL_MS — fetch
  // anyway: on account pages that is the one place the flag can come from
  // at all (a member landing on an account page first must still get the
  // bar).
  const badgeOwned = bootsMemberData || Boolean(cached);
  if (badgeOwned && cardExpiryFresh(cached)) return;

  fetchSummary(header, config, badgeOwned);
}

/* The one box/summary GET. `badgeOwned` = the broadcast or the cache already
 * paints the badge and avatar, so only the card-expiry surfaces repaint. */
function fetchSummary(header, config, badgeOwned) {
  let url = config.summaryUrl;
  if (!url) return;
  const token = previewToken();
  if (token) {
    // Past its expiry a preview sends nothing; the bar says it expired.
    if (window.TWC_PREVIEW.expired) return;
    url += `${url.indexOf('?') === -1 ? '?' : '&'}twc_preview=${encodeURIComponent(token)}`;
  }

  fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
    .then((response) => (response.ok ? response.json() : null))
    .then((body) => {
      const data = body && body.data;
      // A 404 (no open campaign) or an error body lands here as null: treat
      // the flag as still-unknown — no cache write, so the next page retries.
      // Writing a null now would poison the cache into "card fine" for the
      // whole session.
      if (!data || typeof data.count === 'undefined') return;
      // A payload that omits cardExpiry maps to undefined, which preserves
      // whatever the cache holds instead of erasing it (and keeps the key
      // absent, so the fetch retries next page).
      const cardExpiry = Object.prototype.hasOwnProperty.call(data, 'cardExpiry')
        ? data.cardExpiry || null
        : undefined;
      if (badgeOwned) {
        // Backfill mode: the broadcast (or cached record) owns the badge and
        // avatar — repaint only the card-expiry surfaces, and merge the flag
        // into whatever the cache holds by now without clobbering the
        // broadcast's count (zeroed for paused members).
        const current = readCache();
        writeCache(
          current ? current.count : data.count,
          current ? current.phase : data.phase,
          undefined,
          cardExpiry,
        );
        renderCardAlert(cardExpiry);
        return;
      }
      writeCache(data.count, data.phase, data.avatarUrl || null, cardExpiry);
      renderBadge(header, data.count);
      renderAvatar(header, data.avatarUrl || null);
      renderCardAlert(cardExpiry);
    })
    .catch(() => {
      /* endpoint not deployed yet / offline — bag renders with no numeral */
    });
}

/* ---- Boot -------------------------------------------------------------- */

function init() {
  const header = document.getElementById('header-component');
  if (!header) return;

  if (controller) controller.abort();
  if (memberUnsub) {
    memberUnsub();
    memberUnsub = null;
  }
  controller = new AbortController();
  const { signal } = controller;

  wirePanels(header, signal);
  wireDrawers(header, signal);

  const config = readConfig();
  if (config) initBadge(header, config, signal);
}

function onSectionLoad(event) {
  const target = event.target;
  if (target instanceof Element && target.querySelector('#header-component')) init();
}

function onSectionUnload(event) {
  const target = event.target;
  if (!(target instanceof Element) || !target.querySelector('#header-component')) return;
  if (controller) {
    controller.abort();
    controller = null;
  }
  if (memberUnsub) {
    memberUnsub();
    memberUnsub = null;
  }
}

init();

/* PAGE-LIFETIME SCOPE, deliberately not on the AbortController above: these two
 * hooks have to survive every init()/abort cycle, because their whole job is to
 * re-wire the header after a theme-editor section reload has replaced it. They
 * are registered exactly once per document — the window flag makes that hold
 * even if a second copy of this module is ever evaluated (the same idempotence
 * idiom as `if (global.TwcMemberData) return;` in assets/twc-member-data.js) —
 * so nothing accumulates and there is nothing to clean up: the listeners die
 * with the document, like the navMq listener in that same file. */
if (!window.twcHeaderLifecycleWired) {
  window.twcHeaderLifecycleWired = true;
  document.addEventListener('shopify:section:load', onSectionLoad);
  document.addEventListener('shopify:section:unload', onSectionUnload);
}
